import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Heart, MapPin, Star, Shield, MessageCircle, Share2, 
  Eye, Clock, Gavel, Tag, TrendingUp, CheckCircle2
} from 'lucide-react';
import { CompactCountdown } from './CountdownTimer';
import { formatPrice } from '@/utils/constants';

// Demo images for products without images
const DEMO_IMAGES = [
  "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600",
  "https://images.unsplash.com/photo-1526170375885-4d8ecf77b99f?w=600",
  "https://images.unsplash.com/photo-1585386959984-a4155224a1ad?w=600",
  "https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=600"
];

// BidZuu Logo for watermark
const BIDZUU_LOGO = "https://customer-assets.emergentagent.com/job_auction-plaza-1/artifacts/f3twp3sf_2.png";

// eBay + StockX style - Detailed card with verification badges
export const ProductCardDetailed = ({ listing, onWatchlistToggle, isWatched, onMessageSeller, userCurrency }) => {
  const navigate = useNavigate();
  const isAuction = listing.listing_type === "auction";
  const displayPrice = isAuction ? (listing.current_bid || listing.price) : listing.price;
  const listingCurrency = listing.currency || 'USD';
  const formattedPrice = formatPrice(displayPrice, listingCurrency, userCurrency);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Random rating for demo
  const rating = (3.5 + Math.random() * 1.5).toFixed(1);
  const reviewCount = Math.floor(Math.random() * 500) + 10;

  return (
    <Card 
      className="group overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl border-0 bg-white"
      onClick={() => navigate(`/item/${listing.listing_id}`)}
      data-testid={`product-card-${listing.listing_id}`}
    >
      {/* Image Section - Facebook/Etsy style large image */}
      <div className="relative aspect-square overflow-hidden bg-slate-100">
        <img 
          src={listing.images?.[0] || DEMO_IMAGES[Math.floor(Math.random() * 4)]} 
          alt={listing.title}
          className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
        />
        
        {/* BidZuu Logo Watermark - Top Left */}
        <div 
          className="absolute top-3 left-3 z-10"
          style={{ pointerEvents: 'none' }}
        >
          <img 
            src={BIDZUU_LOGO} 
            alt="" 
            style={{ 
              height: '32px', 
              width: 'auto',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
              opacity: 0.95
            }}
          />
        </div>
        
        {/* Overlay badges - StockX style */}
        <div className="absolute top-12 left-3 flex flex-col gap-2">
          {isAuction && listing.end_date && (
            <Badge className="bg-red-500/90 text-white backdrop-blur-sm">
              <Clock className="h-3 w-3 mr-1" />
              <CompactCountdown endDate={listing.end_date} />
            </Badge>
          )}
          {listing.condition === 'new' && (
            <Badge className="bg-emerald-500/90 text-white backdrop-blur-sm">
              <CheckCircle2 className="h-3 w-3 mr-1" /> New
            </Badge>
          )}
          {listing.reserve_met && (
            <Badge className="bg-blue-500/90 text-white backdrop-blur-sm">
              Reserve Met
            </Badge>
          )}
        </div>

        {/* Action buttons - Etsy/Facebook style */}
        <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button 
            className={`p-2.5 rounded-full bg-white/90 backdrop-blur-sm shadow-lg hover:bg-white transition-all ${isWatched ? 'text-red-500' : 'text-gray-600 hover:text-red-500'}`}
            onClick={(e) => {
              e.stopPropagation();
              onWatchlistToggle?.(listing.listing_id);
            }}
            data-testid={`watchlist-btn-${listing.listing_id}`}
          >
            <Heart className={`h-5 w-5 ${isWatched ? 'fill-current' : ''}`} />
          </button>
          <button 
            className="p-2.5 rounded-full bg-white/90 backdrop-blur-sm shadow-lg hover:bg-white text-gray-600 hover:text-blue-500 transition-all"
            onClick={(e) => {
              e.stopPropagation();
              navigator.share?.({ title: listing.title, url: window.location.origin + `/item/${listing.listing_id}` });
            }}
          >
            <Share2 className="h-5 w-5" />
          </button>
        </div>

        {/* Quick action overlay - eBay style */}
        <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex gap-2">
            <Button 
              className="flex-1 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/item/${listing.listing_id}`);
              }}
            >
              {isAuction ? 'Place Bid' : 'Buy Now'}
            </Button>
            <Button 
              variant="secondary"
              className="rounded-full bg-white/90 hover:bg-white"
              onClick={(e) => {
                e.stopPropagation();
                onMessageSeller?.(listing.seller_id);
              }}
            >
              <MessageCircle className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <CardContent className="p-4">
        {/* Price - eBay/StockX prominent style */}
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="text-2xl font-bold text-slate-900">{formattedPrice}</p>
            {isAuction && (
              <p className="text-xs text-muted-foreground">
                {listing.bid_count} bid{listing.bid_count !== 1 ? 's' : ''} · {listing.current_bid ? 'Current bid' : 'Starting bid'}
              </p>
            )}
          </div>
          {isAuction && listing.reserve_price && !listing.reserve_met && (
            <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
              Reserve not met
            </Badge>
          )}
        </div>

        {/* Title */}
        <h3 className="font-semibold text-slate-800 line-clamp-2 mb-2 group-hover:text-emerald-600 transition-colors">
          {listing.title}
        </h3>

        {/* Condition & Category - Amazon style */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          <Badge variant="secondary" className="text-xs font-normal capitalize">
            {listing.condition?.replace('_', ' ')}
          </Badge>
          <Badge variant="outline" className="text-xs font-normal">
            {listing.category}
          </Badge>
        </div>

        {/* Seller info - eBay style */}
        <div className="flex items-center justify-between pt-3 border-t">
          <div className="flex items-center gap-2">
            <Avatar className="h-7 w-7">
              <AvatarFallback className="bg-slate-100 text-slate-600 text-xs">
                {listing.seller_name?.charAt(0)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span className="text-xs font-medium text-slate-700">{listing.seller_name}</span>
              <div className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                <span className="text-xs text-muted-foreground">{rating} ({reviewCount})</span>
              </div>
            </div>
          </div>
          
          {/* Location - Facebook style */}
          {listing.location?.city && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" />
              <span>{listing.location.city}</span>
              {listing.distance_km && (
                <span className="text-emerald-600 font-medium ml-1">
                  ({(listing.distance_km * 0.621371).toFixed(1)} mi)
                </span>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Amazon style - List view card
export const ProductCardList = ({ listing, onWatchlistToggle, isWatched, userCurrency }) => {
  const navigate = useNavigate();
  const isAuction = listing.listing_type === "auction";
  const displayPrice = isAuction ? (listing.current_bid || listing.price) : listing.price;
  const listingCurrency = listing.currency || 'USD';
  const formattedPrice = formatPrice(displayPrice, listingCurrency, userCurrency);
  
  const rating = (3.5 + Math.random() * 1.5).toFixed(1);
  const reviewCount = Math.floor(Math.random() * 500) + 10;

  return (
    <Card 
      className="overflow-hidden cursor-pointer hover:shadow-lg transition-all border-0 bg-white"
      onClick={() => navigate(`/item/${listing.listing_id}`)}
    >
      <div className="flex">
        {/* Image */}
        <div className="relative w-48 h-48 flex-shrink-0 bg-slate-100">
          <img 
            src={listing.images?.[0] || DEMO_IMAGES[0]} 
            alt={listing.title}
            className="w-full h-full object-cover"
          />
          {/* BidZuu Logo Watermark */}
          <div 
            className="absolute top-2 left-2 z-10"
            style={{ pointerEvents: 'none' }}
          >
            <img 
              src={BIDZUU_LOGO} 
              alt="" 
              style={{ 
                height: '24px', 
                width: 'auto',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                opacity: 0.95
              }}
            />
          </div>
          {isAuction && (
            <Badge className="absolute top-2 right-2 bg-red-500/90">
              <Gavel className="h-3 w-3 mr-1" /> Auction
            </Badge>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-4 flex flex-col">
          <div className="flex-1">
            <h3 className="font-semibold text-lg text-slate-800 hover:text-emerald-600 line-clamp-2 mb-1">
              {listing.title}
            </h3>
            
            {/* Rating - Amazon style */}
            <div className="flex items-center gap-2 mb-2">
              <div className="flex">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className={`h-4 w-4 ${i <= Math.floor(rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`} />
                ))}
              </div>
              <span className="text-sm text-blue-600 hover:underline">{reviewCount} reviews</span>
            </div>

            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{listing.description}</p>

            <div className="flex flex-wrap gap-2 mb-2">
              <Badge variant="secondary" className="text-xs capitalize">{listing.condition?.replace('_', ' ')}</Badge>
              {listing.location?.city && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {listing.location.city}, {listing.location.country}
                  {listing.distance_km && (
                    <span className="text-emerald-600 font-medium">
                      ({(listing.distance_km * 0.621371).toFixed(1)} mi away)
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>

          {/* Price and actions */}
          <div className="flex items-end justify-between">
            <div>
              <p className="text-2xl font-bold text-slate-900">{formattedPrice}</p>
              {isAuction && listing.end_date && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <CompactCountdown endDate={listing.end_date} />
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onWatchlistToggle?.(listing.listing_id);
                }}
              >
                <Heart className={`h-4 w-4 ${isWatched ? 'fill-red-500 text-red-500' : ''}`} />
              </Button>
              <Button 
                size="sm"
                className="bg-emerald-500 hover:bg-emerald-600"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/item/${listing.listing_id}`);
                }}
              >
                {isAuction ? 'Place Bid' : 'Buy Now'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

// Compact card for homepage featured sections
export const ProductCardCompact = ({ listing, onWatchlistToggle, isWatched, userCurrency }) => {
  const navigate = useNavigate();
  const isAuction = listing.listing_type === "auction";
  const displayPrice = isAuction ? (listing.current_bid || listing.price) : listing.price;
  const listingCurrency = listing.currency || 'USD';
  const formattedPrice = formatPrice(displayPrice, listingCurrency, userCurrency);

  return (
    <Card 
      className="group overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border-0"
      onClick={() => navigate(`/item/${listing.listing_id}`)}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        <img 
          src={listing.images?.[0] || DEMO_IMAGES[Math.floor(Math.random() * 4)]} 
          alt={listing.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        
        {/* BidZuu Logo Watermark */}
        <div 
          className="absolute top-2 left-2 z-10"
          style={{ pointerEvents: 'none' }}
        >
          <img 
            src={BIDZUU_LOGO} 
            alt="" 
            style={{ 
              height: '24px', 
              width: 'auto',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
              opacity: 0.95
            }}
          />
        </div>
        
        {/* Badges */}
        <div className="absolute top-2 left-12 flex gap-1">
          {isAuction && listing.end_date && (
            <Badge className="bg-red-500/90 text-white text-xs">
              <Clock className="h-3 w-3 mr-1" />
              <CompactCountdown endDate={listing.end_date} />
            </Badge>
          )}
        </div>

        {/* Wishlist */}
        <button 
          className="absolute top-2 right-2 p-2 rounded-full bg-white/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all hover:bg-white"
          onClick={(e) => {
            e.stopPropagation();
            onWatchlistToggle?.(listing.listing_id);
          }}
        >
          <Heart className={`h-4 w-4 ${isWatched ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} />
        </button>
      </div>

      <CardContent className="p-3">
        <h3 className="font-medium text-sm line-clamp-1 mb-1">{listing.title}</h3>
        <div className="flex items-center justify-between">
          <p className="font-bold text-emerald-600">{formattedPrice}</p>
          {isAuction && listing.bid_count > 0 && (
            <span className="text-xs text-muted-foreground">{listing.bid_count} bids</span>
          )}
        </div>
        {listing.location?.city && (
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
            <MapPin className="h-3 w-3" /> {listing.location.city}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductCardDetailed;
